'use client';

import { useCurrencyFormatter } from '@/ui/hooks/useCurrencyFormatter';
import React from 'react';
import { twJoin } from 'tailwind-merge';
import { useTranslations } from 'next-intl';
import { OrderPriceSummaryProps } from './types/OrderPriceSummaryProps';

export default function OrderPriceSummary({
  costBreakdown,
  currency,
  pricingMode,
  textSize,
}: OrderPriceSummaryProps) {
  const { formatCurrency } = useCurrencyFormatter();
  const t = useTranslations('OrderPriceSummary');
  const totalSavings = costBreakdown.totalSavings || 0;

  const isLargeText = textSize === 'large';
  const subtotalIsExclTax = pricingMode && !pricingMode.pricesIncludeTax;
  const showTotalBeforeTaxes =
    costBreakdown.tax !== 0 &&
    (pricingMode?.displayContext === 'account' ||
      (pricingMode?.displayContext === 'checkout' && !pricingMode.pricesIncludeTax));

  return (
    <dl
      className={twJoin(
        'flex flex-col gap-2 px-4 py-4 sm:px-6',
        isLargeText && 'ml-auto max-w-sm sm:text-md md:text-lg',
      )}
    >
      <div className="flex items-center justify-between">
        <dt>
          {t('Subtotal')}
          {subtotalIsExclTax && <span className="text-xs font-light"> {t('exclTaxes')}</span>}
        </dt>
        <dd>{formatCurrency(costBreakdown.subtotal, { currency })}</dd>
      </div>
      {totalSavings > 0 && (
        <div className="flex items-center justify-between text-[#147E47]">
          <dt>{t('Total Savings')}</dt>
          <dd>- {formatCurrency(totalSavings, { currency })}</dd>
        </div>
      )}
      <div className="flex items-center justify-between">
        <dt>{t('Shipping')}</dt>
        <dd>{formatCurrency(costBreakdown.shipping, { currency })}</dd>
      </div>
      {costBreakdown.shippingDiscount != null && costBreakdown.shippingDiscount > 0 && (
        <div className="flex items-center justify-between text-[#147E47]">
          <dt>{t('freeDelivery')}</dt>
          <dd>- {formatCurrency(costBreakdown.shippingDiscount, { currency })}</dd>
        </div>
      )}
      {showTotalBeforeTaxes && (
        <div className="flex items-center justify-between">
          <dt>{t('totalBeforeTaxes')}</dt>
          <dd>{formatCurrency(costBreakdown.totalWithoutTax, { currency })}</dd>
        </div>
      )}
      <div className="flex items-center justify-between">
        <dt>{t('taxes')}</dt>
        <dd>
          {formatCurrency(Math.abs(costBreakdown.tax) === 0 ? 0 : costBreakdown.tax, { currency })}
        </dd>
      </div>
      <div
        className={twJoin(
          'mt-3 flex items-center justify-between font-bold',
          isLargeText && 'text-md sm:text-lg md:text-xl',
        )}
      >
        <dt>
          {t('Total')}
          {pricingMode?.showInclTaxesOnTotal !== false && costBreakdown.tax !== 0 && (
            <span className="text-xs font-light"> {t('inclTaxes')}</span>
          )}
        </dt>
        <dd>{formatCurrency(costBreakdown.total, { currency })}</dd>
      </div>
    </dl>
  );
}
